import get from 'lodash/get'
/*
  Helper function to allow intersection of an array with primitive types with
  and array with objects, given a indentity key.
  Example:
  [1, 2]⋂[{keyIdentity: 1}, {keyIdentity: 2}, {keyIdentity: 3}] => [{keyIdentity: 1}, {keyIdentity: 2}]
*/
function intersectionBy(arr1 = [], arr2 = [], keyIdentity) {
  return arr1.map((element) => arr2.find((item) => item[keyIdentity] === element))
}

/*
  Helper function to intersect get parameter list with node tree item lists
  on specific object key value
*/
function getNodesByParameters(parameters, nodeItems) {
  return intersectionBy(parameters, nodeItems, 'value')
}

/*
   Takes a list of node tree objects and maps these into simpler representative
   objects containing label and value of the node.
*/
const formatParameters = (nodeList, level) => {
  nodeList = Array.isArray(nodeList) ? nodeList : [nodeList]
  return nodeList.filter(Boolean).map((node) => ({
    label: get(node, 'label'),
    value: get(node, 'value'),
    level,
  }))
}

/*
  Takes a object of node tree parameters and transforms the node objects to a simpler form
*/
function formatNodeParameters({ regions = [], municipalities = [], subareas = [] }) {
  return {
    regions: formatParameters(regions, 1),
    municipalities: formatParameters(municipalities, 2),
    subareas: formatParameters(subareas, 3),
  }
}

/*
  Takes a parameterListObject and changes the keys to their short representation
  r = regions, m = municipalities, sa = subareas
*/
function formatParameterResponse(parameterListObject = {}) {
  return {
    r: parameterListObject.regions || [],
    m: parameterListObject.municipalities || [],
    sa: parameterListObject.subareas || [],
  }
}

/*
   Specialized resolver for location parameter tree structures used by search component
   Initialized by providing the object containing the tree structure that will be used
   for mapping GET parameters to correct node data e.g. labels and values.

   @param   {parameterData} : Node[items: Array[Node]]
   @return  {Function}
  */
function TreeNodeResolver(parameterData) {
  /*
   Given region GET parameter find all the correct region Tree nodes
   @param {getParams} : Object[ r: Array, m: Array, sa: Array]
   @return {Object[regions: Array]}
  */
  function getRegionNodes(getParams) {
    const regions = getNodesByParameters(getParams.r, parameterData.items)
    if (!regions) {
      throw Error(`Region node(s) missing in parameterData ${getParams.r}`)
    }
    return { regions }
  }

  /*
   Given municipality GET parameter find all the correct region and municipality Tree nodes
   @param {getParams} : Object[ r: Array, m: Array, sa: Array]
   @return {Object[region: Array, municipality: Array]}
  */
  function getMunicipalityNodes(getParams) {
    const { regions } = getRegionNodes(getParams)
    if (regions.length !== 1) {
      throw Error('Municipality level cannot have multiple regions as parent node')
    }
    const municipalities = getNodesByParameters(getParams.m, regions[0].items)

    if (!municipalities) {
      throw Error(`Municipality node(s) missing in parameterData for region ${regions[0].value}`)
    }

    return { regions, municipalities }
  }

  /*
   Given subarea GET parameter find all the correct region, municipality and subarea Tree nodes
   @param {getParams} : Object[ r: Array, m: Array, sa: Array]
   @return {Object[region: Array, municipality: Array, subarea: Array]}
  */
  function getSubareaNodes(getParams) {
    const { regions, municipalities } = getMunicipalityNodes(getParams)

    if (municipalities.length !== 1) {
      throw Error('Subarea level cannot have multiple municipalities as parent node')
    }

    const subareas = getNodesByParameters(getParams.sa, municipalities[0].items)

    if (!subareas) {
      throw Error(
        `Subarea node(s) missing in parameterData for municipality ${municipalities[0].value}`,
      )
    }

    return { regions, municipalities, subareas }
  }

  /*
   Given location GET parameter object, the correct parameter level is identified
   and all nodes from the tree are found and mapped to a format needed by Tree.js
   @param {getParams} : Object[r: Array[String], m: Array[String], sa: Array[String]]
   @return {
     r: Array[Object[label, value]],
     m: Array[Object[label, value]],
     sa: Array[Object[label, value]],
     parameter_level: String
    }
  */
  const paramNotEmpty = (param) => Array.isArray(param) && param.filter(Boolean).length > 0

  return (getParams) => {
    switch (true) {
      case paramNotEmpty(getParams.sa):
        return {
          ...formatParameterResponse(formatNodeParameters(getSubareaNodes(getParams))),
          parameter_level: 'sa',
        }
      case paramNotEmpty(getParams.m):
        return {
          ...formatParameterResponse(formatNodeParameters(getMunicipalityNodes(getParams))),
          parameter_level: 'm',
        }
      case paramNotEmpty(getParams.r):
        return {
          ...formatParameterResponse(formatNodeParameters(getRegionNodes(getParams))),
          parameter_level: 'r',
        }
      default:
        return { ...formatParameterResponse(), parameter_level: 'r' }
    }
  }
}

export default TreeNodeResolver
